import { useState } from "react";
import { useSignIn } from "@clerk/clerk-react";
import { useToast } from "@/hooks/components/common/toast/useToast";
import type { AuthResponse } from "@/types/hooks/auth";

export const useResendCode = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { signIn } = useSignIn();
    const showToast = useToast();

    const resendCode = async (email: string): Promise<AuthResponse> => {
        if (!signIn || !email) {
            return { success: false, error: 'Session expired' };
        }

        setIsLoading(true);
        try {
            // Create a fresh sign-in attempt with the same email
            await signIn.create({
                strategy: 'reset_password_email_code',
                identifier: email
            });

            // Ensure email is still in storage
            localStorage.setItem('resetEmail', email);

            showToast({
                type: 'success',
                title: 'Code resent',
                message: 'A new verification code has been sent to your email'
            });

            return { success: true };
        } catch (err) {
            const error = err instanceof Error ? err.message : 'Failed to resend code';

            showToast({
                type: 'error',
                title: 'Failed to resend code',
                message: error
            });

            return { success: false, error };
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        resendCode
    };
};